'use client'

import * as React from 'react'
import { AlertTriangle, Ban, HelpCircle, TrendingDown } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { TechnicalDetailsDisclosure } from '@/components/ui/technical-details-disclosure'
import {
  creditStateMeta,
  resolveCreditState,
  type AiAllowance,
  type AiCreditState,
} from '@/lib/ai/credit-state'
import {
  AI_OBSERVE_MODE_COPY,
  creditsSentence,
  blockedCopy,
} from '@/lib/ai/quota-copy'
import { AiRemedyList } from './ai-remedy-list'
import type { AiError } from '@/lib/ai/errors'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'

/**
 * The inline banner: "here is where you stand, and here is what you can do".
 *
 * Sits above a generator, inside a wizard step, or at the top of a settings
 * pane. Everything it says comes from `lib/ai/quota-copy.ts`; it composes no
 * sentences of its own.
 *
 * ── What renders when ───────────────────────────────────────────────────────
 *   healthy   → NOTHING. Silence is success; a green "all good" banner on
 *               every AI surface is noise that trains people to ignore the
 *               amber one.
 *   warning / exhausted / degraded → always.
 *   unknown   → only with `showUnknown`. On a generator, a probe that could
 *               not answer should fail open quietly (the server still enforces
 *               the ceiling, so nothing is bypassed). On a surface that exists
 *               to REPORT standing — the settings usage pane, an admin
 *               console — silence would itself be the false green, so those
 *               callers opt in and get the honest "could not be checked"
 *               sentence. Either way `unknown` never borrows healthy's words
 *               or healthy's colour.
 *
 * ── Provider text ───────────────────────────────────────────────────────────
 * Plain sentence on top; raw upstream detail ONLY inside the collapsed
 * disclosure. `lib/ai/errors.ts` already guarantees `AiError.headline` is
 * SELECTED from a fixed table and never composed from server input, and
 * `TechnicalDetailsDisclosure` UNMOUNTS its content while collapsed (not
 * merely hides it), so "gemini-3.1-pro-preview: 429 RESOURCE_EXHAUSTED at
 * generativelanguage.googleapis.com" is not Ctrl-F or screen-reader reachable
 * until someone deliberately opens it.
 */
export interface AiStateAlertProps {
  allowance: AiAllowance
  /**
   * A typed failure from `lib/ai/errors.ts`. When present its raw detail is
   * offered under a collapsed disclosure. Never build a second error path —
   * lift whatever you caught with `toAiError()`.
   */
  error?: AiError | null
  /** Provider slug to highlight in the settings deep link. */
  provider?: string | null
  /** Render the `unknown` state instead of staying silent. See above. */
  showUnknown?: boolean
  className?: string
}

const STATE_ICON: Record<AiCreditState, React.ComponentType<{ className?: string }>> = {
  healthy: HelpCircle,
  warning: AlertTriangle,
  exhausted: Ban,
  degraded: TrendingDown,
  unknown: HelpCircle,
}

export function AiStateAlert({
  allowance,
  error,
  provider,
  showUnknown = false,
  className,
}: AiStateAlertProps) {
  // Before the early returns below: a hook is called on every render.
  const { t } = useTranslation()
  const state = resolveCreditState(allowance)

  if (state === 'healthy') return null
  if (state === 'unknown' && !showUnknown) return null

  const meta = creditStateMeta(state)
  // Which ceiling blocked decides the words, not just the state — the two
  // reset on different clocks. See blockedCopy().
  const copy = blockedCopy(state, allowance.blockedBy)
  const Icon = STATE_ICON[state]

  // Null when withheld OR absent — see creditsSentence. Never coalesced to 0.
  const figure = creditsSentence({
    creditsRemaining: allowance.creditsRemaining,
    numberVisible: allowance.numberVisible,
  })

  return (
    <Alert
      variant={meta.alertVariant}
      data-testid="ai-state-alert"
      data-state={state}
      // Observe mode: the ceiling is measured but not applied. Surfaces that
      // gate a button read this rather than assuming `exhausted` means blocked.
      data-enforcing={String(allowance.enforcing)}
      className={cn(className)}
    >
      <Icon aria-hidden="true" />
      <AlertTitle data-testid="ai-state-alert-headline">
        {copy.headline}
      </AlertTitle>
      <AlertDescription>
        <p>{copy.body}</p>
        {figure !== null && <p>{figure}</p>}
        {!allowance.enforcing && <p>{AI_OBSERVE_MODE_COPY.headline}</p>}
        <AiRemedyList remedies={allowance.remedies} provider={provider} />
        {error && (
          <TechnicalDetailsDisclosure
            className="mt-2 w-full"
            label={t('aiAlert.technicalDetails')}
            itemCount={1}
            copyText={error.technical}
            sections={[
              {
                title: t('aiAlert.reportedDetail'),
                children: (
                  <pre
                    dir="ltr"
                    tabIndex={0}
                    aria-label={t('aiAlert.rawReportedDetail')}
                    className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono text-[11px]"
                  >
                    {error.technical}
                  </pre>
                ),
              },
            ]}
          />
        )}
      </AlertDescription>
    </Alert>
  )
}
